import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { tradingAPI, paymentAPI } from '../lib/api';
import { formatCurrency, getPnLColor } from '../lib/utils';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  TrendingUp, Wallet, History, User, LogOut, Menu, X, Home, Calendar, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { logout, user } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
    { path: '/history', label: 'Trade History', icon: <History className="w-5 h-5" /> },
    { path: '/account', label: 'Account', icon: <User className="w-5 h-5" /> },
  ];

  // Add Admin Panel link for admin users
  if (user?.is_admin) {
    navItems.push({ path: '/admin', label: 'Admin Panel', icon: <TrendingUp className="w-5 h-5" /> });
  }

  return (
    <>
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setOpen(false)} 
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 
        bg-[#0f0f11] border-r border-zinc-800 
        transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 flex flex-col
      `}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <button
            onClick={logout}
            className="nav-item w-full text-red-400 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

// MT5-style Summary Row
function SummaryRow({ label, value, isProfit = false, isNegative = false }) {
  let valueColor = 'text-white';
  if (isProfit) {
    valueColor = value >= 0 ? 'text-green-400' : 'text-red-400';
  } else if (isNegative) {
    valueColor = 'text-red-400';
  }

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0.00';
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (num < 0) return `-${formatted}`;
    return formatted;
  };

  return (
    <div className="flex justify-between items-center py-1.5 border-b border-zinc-800/50 last:border-b-0">
      <span className="text-zinc-400 text-sm">{label}:</span>
      <span className={`font-mono text-sm ${valueColor}`}>{formatNumber(value)}</span>
    </div>
  );
}

// MT5-style Trade Item
function HistoryTradeItem({ trade }) {
  const formatPrice = (p) => {
    if (!p) return '0.00';
    if (p >= 100) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return p.toFixed(4);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(',', '');
  };

  return (
    <div className="flex justify-between items-start py-3 border-b border-zinc-800/50 last:border-b-0">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">{trade.pair},</span>
          <span className={trade.direction === 'buy' ? 'text-blue-400' : 'text-red-400'}>
            {trade.direction} {trade.volume?.toFixed(2)}
          </span>
        </div>
        <div className="text-zinc-400 text-sm font-mono mt-0.5">
          {formatPrice(trade.entry_price)} → {formatPrice(trade.current_price)}
        </div>
      </div>
      <div className="text-right">
        <div className="text-zinc-500 text-xs mb-0.5">
          {formatDateTime(trade.closed_at)}
        </div>
        <div className={`font-mono font-medium ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trade.pnl >= 0 ? '' : '-'}{Math.abs(trade.pnl).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

// MT5-style Time Period Filter
function TimePeriodFilter({ selectedPeriod, onPeriodChange, customDates, onCustomDatesChange }) {
  const [showCustom, setShowCustom] = useState(false);
  
  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week', label: 'Week' },
    { id: 'month', label: 'Month' },
    { id: 'all', label: 'All' },
    { id: 'custom', label: 'Custom' },
  ];

  const handlePeriodClick = (periodId) => {
    if (periodId === 'custom') {
      setShowCustom(!showCustom);
      if (!showCustom) {
        onPeriodChange('custom');
      }
    } else {
      setShowCustom(false);
      onPeriodChange(periodId);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="mb-4">
      {/* Period Buttons - MT5 Style */}
      <div className="flex flex-wrap gap-1 mb-3 bg-zinc-900/50 p-1 rounded-lg">
        {periods.map((period) => (
          <button
            key={period.id}
            onClick={() => handlePeriodClick(period.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
              selectedPeriod === period.id
                ? 'bg-blue-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            {period.label}
          </button>
        ))}
      </div>

      {/* Custom Date Range Picker */}
      {(showCustom || selectedPeriod === 'custom') && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-400 text-sm">From:</span>
            <Input
              type="date"
              value={customDates.startDate}
              onChange={(e) => onCustomDatesChange({ ...customDates, startDate: e.target.value })}
              className="w-36 h-8 text-sm bg-zinc-800 border-zinc-700"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 text-sm">To:</span>
            <Input
              type="date"
              value={customDates.endDate}
              onChange={(e) => onCustomDatesChange({ ...customDates, endDate: e.target.value })}
              className="w-36 h-8 text-sm bg-zinc-800 border-zinc-700"
            />
          </div>
          <Button
            size="sm"
            onClick={() => onPeriodChange('custom')}
            className="h-8 px-4 bg-blue-600 hover:bg-blue-700"
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}

export default function TradingHistoryPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trades, setTrades] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('positions');
  
  // Time period filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [customDates, setCustomDates] = useState({
    startDate: '',
    endDate: '',
  });
  
  const [summary, setSummary] = useState({
    profit: 0,
    credit: 0,
    deposit: 0,
    withdrawal: 0,
    swap: 0,
    commission: 0,
    balance: 0,
  });

  useEffect(() => {
    fetchData();
  }, [selectedPeriod, customDates]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params based on selected period
      let historyParams = {};
      if (selectedPeriod === 'custom' && (customDates.startDate || customDates.endDate)) {
        historyParams = {
          startDate: customDates.startDate || undefined,
          endDate: customDates.endDate || undefined,
        };
      } else if (selectedPeriod && selectedPeriod !== 'all') {
        historyParams = { period: selectedPeriod };
      }
      
      const [tradesRes, transactionsRes] = await Promise.all([
        tradingAPI.getTradeHistory(historyParams),
        paymentAPI.getTransactions(),
      ]);
      
      setTrades(tradesRes.data);
      setTransactions(transactionsRes.data);
      
      // Calculate summary from filtered trades
      const totalProfit = tradesRes.data.reduce((sum, t) => sum + (t.pnl || 0), 0);
      const totalCommission = tradesRes.data.reduce((sum, t) => sum + (t.commission || 0), 0);
      const totalSwap = tradesRes.data.reduce((sum, t) => sum + (t.swap || 0), 0);
      
      const deposits = transactionsRes.data
        .filter(t => t.type === 'deposit' && t.status === 'completed')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const withdrawals = transactionsRes.data
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const balance = user?.account_type === 'demo' ? user?.demo_balance : user?.balance;
      
      setSummary({
        profit: totalProfit,
        credit: 0, // Credit feature not implemented
        deposit: deposits,
        withdrawal: -withdrawals,
        swap: -totalSwap,
        commission: totalCommission,
        balance: balance || 0,
      });
    } catch (error) {
      toast.error('Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== 'custom') {
      setCustomDates({ startDate: '', endDate: '' });
    }
  };

  const tabs = [
    { id: 'positions', label: 'POSITIONS' },
    { id: 'orders', label: 'ORDERS' },
    { id: 'deals', label: 'DEALS' },
  ];

  // Get period label for display
  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'week': return 'Last 7 Days';
      case 'month': return 'Last 30 Days';
      case 'custom': 
        if (customDates.startDate && customDates.endDate) {
          return `${customDates.startDate} to ${customDates.endDate}`;
        }
        return 'Custom Range';
      default: return 'All Time';
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="history-page">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="flex-1 min-w-0 bg-[#0a0a0b]">
        {/* Header */}
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-white font-medium">History</h1>
              <span className="text-zinc-500 text-sm">{getPeriodLabel()} • {trades.length} trades</span>
            </div>
          </div>
        </header>

        {/* Time Period Filter - MT5 Style */}
        <div className="p-4 border-b border-zinc-800">
          <TimePeriodFilter
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDates={customDates}
            onCustomDatesChange={setCustomDates}
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-white border-b-2 border-white'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <div className="p-4">
            {/* Summary Section */}
            <div className="mb-6 bg-zinc-900/30 rounded-lg p-3">
              <h3 className="text-zinc-400 text-xs font-medium mb-2 uppercase tracking-wider">
                Summary ({getPeriodLabel()})
              </h3>
              <SummaryRow label="Profit" value={summary.profit} isProfit />
              <SummaryRow label="Credit" value={summary.credit} />
              <SummaryRow label="Deposit" value={summary.deposit} />
              <SummaryRow label="Withdrawal" value={summary.withdrawal} isNegative={summary.withdrawal < 0} />
              <SummaryRow label="Swap" value={summary.swap} isNegative={summary.swap < 0} />
              <SummaryRow label="Commission" value={summary.commission} />
              <SummaryRow label="Balance" value={summary.balance} />
            </div>

            {/* Content based on active tab */}
            {activeTab === 'positions' && (
              <div>
                {trades.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No closed positions for {getPeriodLabel().toLowerCase()}</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[calc(100vh-480px)]">
                    <div>
                      {trades.map((trade) => (
                        <HistoryTradeItem key={trade.id} trade={trade} />
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}

            {activeTab === 'orders' && (
              <div className="text-center py-8 text-zinc-500">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No pending orders</p>
              </div>
            )}

            {activeTab === 'deals' && (
              <div>
                {trades.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No deals for {getPeriodLabel().toLowerCase()}</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[calc(100vh-480px)]">
                    <div>
                      {trades.map((trade) => (
                        <HistoryTradeItem key={trade.id} trade={trade} />
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
